import {FC, Fragment, useMemo} from 'react';
import {Autocomplete, Button, FormControl, FormHelperText, MenuItem, Select, TextField} from '@mui/material';
import {Controller, useForm} from 'react-hook-form';
import InputLabel from '@mui/material/InputLabel';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  contractDataImportsAddModalClose,
  useContractDataImportsAddModalStore,
} from '../../stores/contract-data-imports-add-modal.store';
import {ContractFormFileField} from '../contract-form-file-field';
import {useGetDictsServiceTypesQuery} from '../../../../global/services/service-types';
import {filter, get, isNil, keyBy, keys, mapValues, size, toNumber} from 'lodash';
import {ContractModal} from '../contract-modal';
import {
  useGetImportsRefresh,
  usePostImportsExcelApprove,
  usePostImportsExcelSr,
} from '../../../../global/services/imports';
import {useParams} from 'react-router-dom';
import {useRefreshCompanyTariff} from '../../../company-tariff';

const IMPORT_TYPES = [
  {value: 'serviceRequest', label: 'Заявки'},
  {value: 'serviceRequestThirdSide', label: 'Заявки (из сторонней системы)'},
  {value: 'workData', label: 'Данные опроса ПУ'},
  {value: 'approved', label: 'Принято заказчиком'},
] as const;

const DEFAULT_VALUES = {
  deviceType: '',
  importType: '',
  description: '',
  file: null,
} as const;

interface ImportTypeMapping {
  loadType: 'THIRD_SIDE' | 'REGISTRY' | 'WORKER';
  docType: 'SERVICE_REQUESTS';
}

interface FromValues {
  description: string;
  file: File | null;
  importType: (typeof IMPORT_TYPES)[number]['value'] | '';
  deviceType?: string;
}

export const AddImportsModal: FC = () => {
  const {id} = useParams();
  const {mutate: importsExcelApproveMutate, loading: importsExcelApproveLoading} = usePostImportsExcelApprove();
  const {mutate: importsExcelSrMutate, loading: importsExcelSrLoading} = usePostImportsExcelSr();
  const {data} = useGetDictsServiceTypesQuery({});
  const refreshImports = useGetImportsRefresh();
  const refreshCompanyTariff = useRefreshCompanyTariff();
  const types = get(data, 'content', []);
  const open = useContractDataImportsAddModalStore((s) => s.open);
  const formApi = useForm<FromValues>({
    mode: 'onChange',
    defaultValues: DEFAULT_VALUES,
  });
  const {isDirty, isValid} = formApi.formState;
  const loading = importsExcelApproveLoading || importsExcelSrLoading;

  const {autocompleteOptions, labels} = useMemo(() => {
    const filtered = filter(types, (item) => isNil(item.higherCode));
    const result = mapValues(keyBy(filtered, 'code'), 'name');
    return {autocompleteOptions: keys(result), labels: result};
  }, [data?.content]);

  const handleClose = () => {
    formApi.reset(DEFAULT_VALUES);
    contractDataImportsAddModalClose();
  };

  const afterMutateCallback = () => {
    refreshImports();
    refreshCompanyTariff();
    handleClose();
  };

  const handleFormSubmit = formApi.handleSubmit(async (data) => {
    if (!data.file) return;
    if (data.importType === 'approved') {
      await importsExcelApproveMutate(
        [{docFile: data.file}, {comment: data.description, contractId: toNumber(id)}],
        afterMutateCallback,
      );
    } else {
      const {loadType, docType} = mapImportType(data.importType);
      await importsExcelSrMutate(
        [
          {
            contractId: toNumber(id),
            docType,
            loadType,
            comment: data.description,
            deviceType: data.deviceType ?? '',
          },
          {docFile: data.file},
        ],
        afterMutateCallback,
      );
    }
  });

  return (
    <ContractModal
      title="Добавить документ"
      open={open}
      footer={
        <Fragment>
          <Button variant="outlined" color="error" onClick={handleClose}>
            Отменить
          </Button>
          <LoadingButton
            variant="contained"
            onClick={handleFormSubmit}
            disabled={!isDirty || !isValid}
            loading={loading}
          >
            Добавить
          </LoadingButton>
        </Fragment>
      }
    >
      <Controller
        control={formApi.control}
        name="importType"
        rules={{required: 'Обязательно для заполнения'}}
        render={({field: {value, onChange}, fieldState: {error}}) => (
          <FormControl fullWidth size="small" error={!!error}>
            <InputLabel>Тип документа</InputLabel>
            <Select label="Тип документа" value={value} onChange={onChange}>
              {IMPORT_TYPES.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
            {!!error && <FormHelperText>{error.message}</FormHelperText>}
          </FormControl>
        )}
      />

      <Controller
        control={formApi.control}
        name="file"
        rules={{required: 'Выберите файл'}}
        render={({field: {onChange, value}, fieldState: {error}}) => (
          <ContractFormFileField accept=".xls,.xlsx" file={value} error={error} onChange={onChange} />
        )}
      />

      <Controller
        name="deviceType"
        control={formApi.control}
        rules={{validate: validateDeviceType}}
        render={({field: {value, onChange}, fieldState: {error}}) => (
          <FormControl fullWidth size="small" error={!!error}>
            <Autocomplete
              hidden={!isServiceRequest(formApi.watch('importType'))}
              disabled={!autocompleteOptions.length}
              onChange={(_, newValue) => onChange(newValue)}
              value={value}
              getOptionLabel={(option) => get(labels, option, '')}
              options={autocompleteOptions}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Тип устройства"
                  size="small"
                  error={!!error}
                  helperText={error ? error.message : ''}
                />
              )}
            />
          </FormControl>
        )}
      />

      <Controller
        control={formApi.control}
        name="description"
        rules={{validate: validateDescription}}
        render={({field, fieldState: {error}}) => (
          <TextField
            label="Комментарий"
            fullWidth
            multiline
            rows={4}
            {...field}
            error={!!error}
            helperText={error?.message}
          />
        )}
      />
    </ContractModal>
  );
};

function validateDeviceType(value: string | undefined, {importType}: FromValues) {
  if (isServiceRequest(importType) && !value) return 'Обязательно для заполнения';
}

function validateDescription(value: string | undefined, {importType}: FromValues) {
  if (importType === 'approved' && !value) return 'Обязательно для заполнения';
  if (size(value) > 400) return 'Максимум 400 символов';
  return true;
}

function isServiceRequest(importType: FromValues['importType']) {
  return ['serviceRequest', 'serviceRequestThirdSide'].includes(importType);
}

function mapImportType(importType: FromValues['importType']): ImportTypeMapping {
  const loadType =
    importType === 'serviceRequestThirdSide' ? 'THIRD_SIDE' : importType === 'serviceRequest' ? 'REGISTRY' : 'WORKER';

  const docType = 'SERVICE_REQUESTS';

  return {loadType, docType};
}
